import React, { useMemo } from "react";
import { getSummarizationJobTimeLabel } from "./summarizationJobUtils";
import { transcriptNoteModalColumnText } from "./supportStyles";

const SummarizationJobPicker = ({
  sortedJobs,
  selectedJobName,
  onSelectJob,
  summarizationLoading,
  summarizationError,
}) => {
  const jobCount = sortedJobs.length;
  const selectedIndex = useMemo(
    () =>
      sortedJobs.findIndex((job) => job.summarize_job_name === selectedJobName),
    [sortedJobs, selectedJobName]
  );
  const showScrollHint = jobCount > 2;

  if (summarizationLoading) {
    return (
      <p className="text-xs text-gray-500 shrink-0">Loading summarization jobs…</p>
    );
  }

  if (summarizationError) {
    return (
      <p className={`${transcriptNoteModalColumnText} text-red-600 text-sm shrink-0`}>
        {summarizationError}
      </p>
    );
  }

  if (jobCount === 0) {
    return (
      <p className="text-xs text-gray-500 shrink-0">
        No summarization jobs for this patient.
      </p>
    );
  }

  return (
    <div className="shrink-0 space-y-1">
      <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
        <span className="font-medium text-gray-700">
          {jobCount} summarization {jobCount === 1 ? "job" : "jobs"}
        </span>
        {selectedIndex >= 0 ? (
          <span className="text-gray-500">
            Selected {selectedIndex + 1} of {jobCount}
          </span>
        ) : (
          <span className="text-gray-500">Select one to compare</span>
        )}
      </div>

      <div className="relative rounded-md border border-gray-200 bg-slate-50/50">
        <div
          className={`max-h-28 overflow-y-auto divide-y divide-gray-100 ${
            showScrollHint ? "pb-1" : ""
          }`}
        >
          {sortedJobs.map((job) => {
            const jobName = job.summarize_job_name || `job-${job.patient_id}`;
            const isSelected = job.summarize_job_name === selectedJobName;

            return (
              <button
                key={jobName}
                type="button"
                onClick={() => onSelectJob(job.summarize_job_name)}
                className={`w-full text-left px-2.5 py-1.5 transition-colors ${
                  isSelected
                    ? "bg-purple-50 border-l-2 border-l-ktx-purple"
                    : "bg-white hover:bg-slate-50 border-l-2 border-l-transparent"
                }`}
              >
                <p className="text-xs font-medium text-gray-900">
                  {getSummarizationJobTimeLabel(job)}
                </p>
                {job.summarize_job_name ? (
                  <p className="text-[11px] text-gray-500 mt-0.5 truncate font-mono">
                    {job.summarize_job_name}
                  </p>
                ) : null}
              </button>
            );
          })}
        </div>

        {showScrollHint ? (
          <p className="pointer-events-none absolute inset-x-0 bottom-0 px-2 py-1 text-center text-[10px] font-medium text-gray-500 bg-gradient-to-t from-white via-white/95 to-transparent">
            Scroll for more jobs
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default SummarizationJobPicker;
